import { Component, OnInit } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { FormBuilder, Validators } from '@angular/forms';
import { AppUtils } from '../../../core/config/app.utils';
import { ActionAlert } from '../../../core/dto/actionAlert';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Location } from '@angular/common';
import { LoadingService } from '../../../core/services/loading.service';
import { AgentService } from '../../../core/services/agent.service';
import { Pagination } from '../../../core/dto/pagination';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { DefaultPageSize } from '../../../core/dto/defaultPage.size';
import { ContactService } from '../../../core/services/contact.service';
import { AddEditContactComponent } from './add-edit-contact/add-edit-contact.component';
import { NzModalService } from 'ng-zorro-antd/modal';

@Component({
  selector: 'app-add-edit-agent',
  templateUrl: './add-edit-agent.component.html',
  styleUrls: ['./add-edit-agent.component.scss'],
})
export class AddEditAgentComponent implements OnInit {
  agentId: any = undefined;
  isAdd = false;
  agentForm: any;
  formErrors: any = {
    name: '',
  };
  actionMessage = new ActionAlert();
  singleAgent: any = {};
  contactSearch = '';
  allContacts: any;
  pageSize = new DefaultPageSize();
  sortField: any;
  sortOrder: any;
  allCustomers: any;

  sortColumn = {
    compareDate: (a: any, b: any) => {
      return (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any);
    },
    compareName: (a: any, b: any) => a.name.localeCompare(b.name),
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private agentService: AgentService,
    private contactService: ContactService,
    private msg: NzMessageService,
    private _location: Location,
    private loading: LoadingService,
    private router: Router,
    private nzModalService: NzModalService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    // Set Add or Update Page
    this.agentId = this.route.snapshot.paramMap.get('id');
    this.isAdd = !this.agentId;
    if (!this.isAdd) {
      this.loadAgentByID();
    }

    this.agentForm = this.fb.group({
      name: ['', [Validators.required]],
    });

    this.agentForm.valueChanges.subscribe((form: any) => {
      AppUtils.getFormErrors(this.agentForm, this.formErrors);
    });
  }

  async loadAgentByID(): Promise<any> {
    try {
      this.singleAgent = await this.agentService.getAgentByID(this.agentId);
      this.agentForm.patchValue(this.singleAgent);
    } catch (e) {
      this.msg.error(this.translocoService.translate('agents.addEdit.messages.agentNotFound'));
      this._location.back();
    }
  }

  async addAgent(): Promise<void> {
    this.loading.start();
    try {
      const payload = this.agentForm.getRawValue();
      const data = await this.agentService.createAgent(payload);
      this.agentForm.reset();
      this.router.navigate(['/admin/agents/edit/', data._id]);
      this.actionMessage = new ActionAlert(
        true,
        this.isAdd,
        this.translocoService.translate('agents.addEdit.messages.added'),
        'success'
      );
      this.loading.stop();
    } catch (e: any) {
      this.actionMessage = new ActionAlert(
        true,
        this.isAdd,
        e.error?.message || this.translocoService.translate('agents.addEdit.messages.notAdded'),
        'error'
      );
      this.loading.stop();
    }
  }

  async editAgent(): Promise<any> {
    this.loading.start();
    try {
      const payload = {
        _id: this.agentId,
        ...this.agentForm.getRawValue(),
      };
      const data = await this.agentService.updateAgent(payload);
      this.actionMessage = new ActionAlert(
        true,
        !this.isAdd,
        this.translocoService.translate('agents.addEdit.messages.updated'),
        'success'
      );
      this.loading.stop();
    } catch (e: any) {
      this.actionMessage = new ActionAlert(
        true,
        !this.isAdd,
        e.error?.message || this.translocoService.translate('agents.addEdit.messages.notUpdated'),
        'error'
      );
      this.loading.stop();
    }
  }

  search(): void {
    this.getContactsPaged();
  }

  reset(): void {
    this.contactSearch = '';
    this.getContactsPaged();
  }

  getContactsPaged(): void {
    this.loading.start();
    const payload = Object.assign({}, new Pagination(this.pageSize), {
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      keyword: this.contactSearch,
      agent: this.agentId,
    });

    this.contactService.getAllContacts(payload).then(res => {
      this.allContacts = res.data;
      this.pageSize.length = res.metadata[0]?.total;
      this.loading.stop();
    });
  }

  onTableParamsChange(params: NzTableQueryParams): void {
    this.pageSize.pageSize = params.pageSize;
    this.pageSize.pageIndex = params.pageIndex;

    const currentSort = params.sort.find(item => item.value !== null);
    this.sortField = (currentSort && currentSort.key) || null;
    this.sortOrder =
      (currentSort && AppUtils.getSortOrder(currentSort.value)) || null;
    this.getContactsPaged();
  }

  addEditContact(agent: any, mode: string, contactData?: any): void {
    const titleText = mode !== 'edit'
      ? this.translocoService.translate('agents.contactModal.addTitle')
      : this.translocoService.translate('agents.contactModal.editTitle');
    this.nzModalService
      .create({
        nzTitle: titleText,
        nzWidth: '450px',
        nzContent: AddEditContactComponent,
        nzFooter: null,
        nzComponentParams: {
          agent,
          mode,
          contactData,
        },
      })
      .afterClose.subscribe(data => {
        console.log(data);
        if (data === "success") {
          this.actionMessage = new ActionAlert(
            true,
            true,
            mode === 'new'
              ? this.translocoService.translate('agents.addEdit.messages.contactAdded')
              : this.translocoService.translate('agents.addEdit.messages.contactUpdated'),
            'success'
          );
        }
        this.getContactsPaged();
      });
  }
}
